<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faArrowDown, faArrowUp, faCalculator, faEdit, faPlus, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { Appointment } from '../../modules/schedulerApi/appointments/appointment'
    import InlineSelect from '../_common/inline-select.svelte'
    import { AppointmentStatusFriendly, AppointmentStatusHelper } from '../../modules/schedulerApi/appointments/appointments.enum'
    import dayjs from 'dayjs'
    import AppointmentFormItems from './appointment-form-items.svelte'
    import { User } from '../../modules/usersApi/users/user'
    import type { AppointmentSugestion } from '../../modules/schedulerApi/appointments/appointmentSugestion'
    import type { Service } from '../../modules/posApi/services/service'
    import { AppointmentItem } from '../../modules/schedulerApi/appointments/appointment-item'
    import AppointmentFormResources from './appointment-form-resources.svelte'
    import { AppointmentResource } from '../../modules/schedulerApi/appointments/appointment-resource'
    import AppointmentFormConsumption from './appointment-form-consumption.svelte'
    import { AppointmentConsumption } from '../../modules/schedulerApi/appointments/appointment-consumption'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { slide } from 'svelte/transition'
    import type { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import type { GridButton } from '../_common/z/zgrid'
    import Zbutton from '../_common/z/zbutton.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'
    import ZContainerRow from '../_common/z/z-container-row.svelte'
    import AppointmentFormBlockSugestions from './appointment-form-block-sugestions.svelte'
    import { ancorarTerminoNoInicio } from './appointment-dates'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let personId: string = null
    export let schedules: Schedule[] = []
    export let entity: Appointment = null
    export let showActs: boolean = false

    let show = false
    let state: 'ready' | 'typing'
    let sugest

    let scheduleOptions: { label: string; value: string }[] = []
    function loadScheduleOptions(_s: Schedule[]) {
        scheduleOptions = []
        for (const schedule of _s) {
            scheduleOptions.push({ value: schedule.scheduleId, label: schedule.label })
        }
        scheduleOptions = scheduleOptions
    }

    $: {
        loadScheduleOptions(schedules)
    }

    let showRecurrency = false
    let showAddService = true

    let appointmentStatus: { value: any; label: string }[] = []

    let days: { label: string; value: number }[] = []
    days.push({ label: 'dom', value: 0 })
    days.push({ label: 'seg', value: 1 })
    days.push({ label: 'ter', value: 2 })
    days.push({ label: 'qua', value: 3 })
    days.push({ label: 'qui', value: 4 })
    days.push({ label: 'sex', value: 5 })
    days.push({ label: 'sab', value: 6 })

    let periods: { label: string; value: number }[] = []
    periods.push({ label: 'toda semana', value: 7 })
    periods.push({ label: 'semana sim/semana não', value: 14 })
    for (let index = 3; index <= 51; index++) {
        periods.push({ label: 'A cada ' + index + ' semanas', value: index * 7 })
    }
    periods.push({ label: 'A cada 52 semanas (1 ano)', value: 52 * 7 })

    // o "vezes" era um adendo colado ao select; com o Zinput o rótulo do campo já diz "Repetir por
    // mais", então a unidade vai junto de cada opção
    // o padrão de AppointmentRepeat é quantity 0, que não tinha opção no select antigo e deixava o
    // campo em branco; com a opção explícita o estado "não repete" fica visível em vez de parecer
    // um campo por preencher
    let repeatQuantities: { label: string; value: number }[] = [{ label: 'Não repetir', value: 0 }]
    for (let index = 1; index <= 52; index++) {
        repeatQuantities.push({ label: index === 1 ? '1 vez' : index + ' vezes', value: index })
    }

    let timeAuto: GridButton = {
        label: '',
        icon: faCalculator,
        callback: onToggleAuto,
        class: entity.auto ? 'appointment-form_auto-on' : 'appointment-form_auto-off',
    }

    export let isDirty = true

    onMount(async () => {
        await evaluateResources()
    })

    function loadStatus() {
        let statusKeys = Object.keys(AppointmentStatusFriendly)
        for (const item of statusKeys) {
            appointmentStatus.push({
                label: AppointmentStatusFriendly[item],
                value: item,
            })
        }
        appointmentStatus = appointmentStatus
    }

    let schedule: Schedule = null
    async function loadSchedule(_scheduleId: string) {
        schedule = schedules.find((el) => el.scheduleId == _scheduleId)
    }

    let newConsumption = false
    async function newConsumptionAdded(event) {
        let consumption: AppointmentConsumption = event.detail
        if (!entity.consumption) entity.consumption = []
        entity.consumption.push(consumption)
        entity.consumption = entity.consumption
        newConsumption = false
    }

    function consumptionUpdated(ev) {
        entity.consumption = (entity.consumption || []).filter((el) => !!el)
        if (ev.detail) {
            entity.consumption = entity.consumption
        }
    }

    function newConsumptionCancelled() {
        newConsumption = false
    }

    function newConsumptionItem() {
        newConsumption = true
    }

    let lockedIDs: string[] = []
    let showResources = false
    let lockedRequestSeq = 0
    async function loadLockedResources() {
        // descarta resposta fora de ordem (ex: horário alterado de novo antes da consulta anterior voltar)
        const seq = ++lockedRequestSeq
        const ids = await window.api.schedulers.resourcesLock.lockedIds(entity.start, entity.end, entity.appointmentId)
        if (seq === lockedRequestSeq) lockedIDs = ids
    }

    // recurso já adicionado que ficou ocupado NÃO é mais removido aqui (antes todos eram apagados em silêncio):
    // a linha mostra o aviso e o salvar bloqueia (ver appointment-form.svelte#checkResourcesAvailability)
    async function evaluateResources() {
        show = true
        try {
            showResources = entity.items.some((item) => item?.service?.needResource) || entity.resources?.length > 0
            // carrega sempre que a seção aparece — antes só quando algum serviço exigia recurso, e um recurso
            // adicionado sem essa exigência nunca era conferido
            if (showResources) await loadLockedResources()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function refreshLockedResources() {
        try {
            await loadLockedResources()
        } catch (error) {
            window.error(error)
        }
    }

    let resourcesEvaluationBounce = null
    function scheduleResourcesEvaluation() {
        if (resourcesEvaluationBounce) clearTimeout(resourcesEvaluationBounce)
        resourcesEvaluationBounce = setTimeout(() => evaluateResources(), 500)
    }

    // qualquer mudança de horário — campos de início/término, término calculado pelos serviços, data alterada
    // fora deste bloco — recarrega os recursos ocupados. o primeiro valor é avaliado pelo onMount.
    let lastRangeKey: string = undefined
    $: onRangeChange(entity?.start, entity?.end)
    function onRangeChange(start: Date, end: Date) {
        const key = start && end ? new Date(start).getTime() + '-' + new Date(end).getTime() : null
        if (key === lastRangeKey) return
        const isFirst = lastRangeKey === undefined
        lastRangeKey = key
        if (!isFirst) scheduleResourcesEvaluation()
    }

    onDestroy(() => {
        if (resourcesEvaluationBounce) clearTimeout(resourcesEvaluationBounce)
    })

    let currentPersonId = null

    function updateTimes() {
        function isValid(time) {
            if (!time) return false
            return !isNaN(new Date('2024-06-12 ' + time)?.getTime())
        }

        // if (isValid(start)) {
        //     const copy = entity.start.toString()
        //     entity.start.setHours(parseInt(start.split(':')[0]), parseInt(start.split(':')[1]), 0)
        //     console.log('start is valid', copy, entity.start)
        // }
        // if (isValid(end)) {
        //     entity.end.setHours(parseInt(end.split(':')[0]), parseInt(end.split(':')[1]), 0)
        // }
        scheduleResourcesEvaluation()
    }

    function setState(s: 'ready' | 'typing') {
        state = s
        dispatch('state', state)
    }

    function dispatchChange() {
        dispatch('change')
    }

    function dispatchSave() {
        dispatch('save', entity)
    }

    function dispatchEditAnother(appointmentId) {
        dispatch('editAnother', appointmentId)
    }

    function dispatchRemove() {
        dispatch('remove', entity)
    }

    function dispatchRemoveAnother(appointmentId) {
        dispatch('removeAnother', { entity, appointmentId })
    }

    let newResource = false
    async function newResourceAdded(event) {
        let resource: AppointmentResource = event.detail
        resource.resourcesLockId = resource.resourceLock.resourcesLockId
        entity.resources.push(resource)
        entity.resources = entity.resources
        newResource = false
    }

    function resourceUpdated(ev) {
        entity.resources = entity.resources.filter((el) => !!el)
        if (ev.detail) {
            let resource: AppointmentResource = ev.detail
            resource.resourcesLockId = resource.resourceLock.resourcesLockId
            entity.resources = entity.resources
        }
    }

    function newResourceCancelled() {
        newResource = false
    }

    function newResourceItem() {
        newResource = true
        refreshLockedResources()
    }

    let lastUserId: string = null
    async function newItemAdded(event) {
        const appointmentItem = event.detail
        addNewItem(
            appointmentItem.user,
            appointmentItem.service,
            appointmentItem.parentServicesOnInvoicesId,
            appointmentItem.invoiceId,
            appointmentItem.groupId,
        )
        setState('ready')
    }

    async function addNewItem(user: User, service: Service, parentServicesOnInvoicesId: string, invoiceId: string, groupId: string) {
        let item = new AppointmentItem()
        // atribui a categoria para que possa ser logada no evento.
        item.service = (await window.api.utils.utils.assignServiceCategoryFromObject([service]))[0]
        item.invoiceId = invoiceId
        item.user = user
        item.label = item.service.name
        item.serviceId = item.service.serviceId
        item.userId = item.user?.userId
        item.parentServicesOnInvoicesId = parentServicesOnInvoicesId
        //item.groupId = groupId
        // comissions
        item.comissionCosts = item.service.comissionCostsAttendant
        item.comissionFixed = item.service.comissionFixedAttendant
        // prévia local com o preço de tabela, só pra exibir uma comissão estimada antes de salvar. O backend
        // IGNORA este price no upsert (desde 26/07/2026 ele espelha comissionPrice) e recalcula tudo em
        // calculateComission, que apura o valor de venda real do cliente.
        item.price = item.service.price
        item.comissionPercentage = (item.price * item.service.comissionPercentageAttendant) / 100
        entity.items.push(item)
        entity.items = entity.items
        lastUserId = item.userId

        // const added = sugestions.find((el) => el.serviceId === service.serviceId)
        // if (added) added.added = true

        await autoFillConsumptionFromRecipe(item)

        sugest.addLastService()
        evaluateAuto()
        await evaluateResources()
        console.log('addnewItem', item)
    }

    // preenchimento automático de consumo (BOM) — só na primeira adição do item, nunca ao editar um já
    // existente (ver updateItem). o atendente pode ajustar/remover livremente depois; "Eu estou" sempre
    // fica em branco pra não induzir erro (ver appointment-form-consumption.svelte).
    async function autoFillConsumptionFromRecipe(item: AppointmentItem) {
        const added = await window.api.utils.utils.buildConsumptionFromItems([item])
        if (added.length === 0) return
        if (!entity.consumption) entity.consumption = []
        entity.consumption.push(...added)
        entity.consumption = entity.consumption
    }

    // remove (agendamento ainda não salvo) ou desvincula (edição de agendamento existente) o consumo que
    // veio automaticamente da receita de um item removido — nunca mexe em consumo lançado manualmente
    // pelo atendente (sourceItem só existe em linhas geradas pelo preenchimento automático).
    function cleanupOrphanRecipeConsumption(items: AppointmentItem[]) {
        if (!entity.consumption || entity.consumption.length === 0) return
        const currentItems = new Set((items || []).filter((el) => !!el))
        let changed = false
        const kept: AppointmentConsumption[] = []
        for (const consumption of entity.consumption) {
            if (consumption.sourceItem && !currentItems.has(consumption.sourceItem)) {
                changed = true
                if (!entity.appointmentId) {
                    continue // criação: ainda não persistiu nada, remove junto com o item
                }
                consumption.performedServiceId = undefined
                consumption.sourceItem = undefined
            }
            kept.push(consumption)
        }
        if (changed) entity.consumption = kept
    }
    $: cleanupOrphanRecipeConsumption(entity.items)

    function updateItem(user: User, service: Service, item: AppointmentItem) {
        if (user) {
            // se foi exclusão user = null
            item.user = user
            item.service = service
            item.label = item.service.name
            item.serviceId = item.service.serviceId
            item.userId = item.user.userId
            item.parentServicesOnInvoicesId = item.parentServicesOnInvoicesId
            //item.groupId = item.groupId // ???
            // comissions
            item.comissionCosts = item.service.comissionCostsAttendant
            item.comissionFixed = item.service.comissionFixedAttendant
            // prévia local — o backend ignora este price e recalcula em calculateComission (ver nota acima)
            item.price = item.service.price
            item.comissionPercentage = (item.price * item.service.comissionPercentageAttendant) / 100
            entity.items = entity.items
            lastUserId = item.userId

            // const added = sugestions.find((el) => el.serviceId === service.serviceId)
            // if (added) added.added = true
            // sugestions = sugestions

            console.log('updateItem', item)
        }
        evaluateAuto()
        evaluateResources()
    }

    function onAddSugestion(ev) {
        let sugestion: AppointmentSugestion = ev.detail
        console.log('sugestion', sugestion)
        addNewItem(sugestion.user, sugestion.service, sugestion.parentServicesOnInvoicesId, sugestion.invoiceId, sugestion.groupId)
    }

    function evaluateAuto() {
        if (entity.auto) {
            // horário inicial vazio (campo limpo): não há de onde calcular o término
            if (!entity.start || isNaN(new Date(entity.start).getTime())) return
            let servicesMinutes = entity.items.filter((el) => !!el).reduce((acc, el) => (acc += el.service.duration), 0)
            if (servicesMinutes < 10) servicesMinutes = 10
            entity.end = new Date(entity.start)
            entity.end.setMinutes(entity.start.getMinutes() + servicesMinutes)
            //end = dayjs(entity.end).format('HH:mm')
        }
    }

    function onToggleAuto() {
        entity.auto = !entity.auto
        evaluateAuto()
        if (entity.auto) evaluateResources()
        timeAuto.class = 'appointment-form_auto-' + (entity?.auto ? 'on' : 'off')
        timeAuto = timeAuto
        console.log('auto', timeAuto.class)
    }

    function itemUpdated(ev) {
        entity.items = entity.items.filter((el) => !!el)
        let appointmentItem = ev.detail
        updateItem(appointmentItem?.user, appointmentItem?.service, appointmentItem)
    }

    function newItemCancelled() {
        setState('ready')
        evaluateAuto()
    }

    function newAppointmentItem() {
        setState('typing')
    }

    function startTimeChanged() {
        entity.auto = false
        updateTimes()
    }
    function endTimeChanged() {
        entity.auto = false
        // o campo de hora edita só HH:mm e mantém o dia do valor que já tinha — ele não conhece o
        // início. sem reancorar, um término herdado de outro dia (1970, por exemplo) continua lá e o
        // salvar acusa "a hora final não pode ser anterior à hora inicial" com horários plausíveis na tela
        entity.end = ancorarTerminoNoInicio(entity.start, entity.end)
        updateTimes()
    }

    onMount(async () => {
        if (entity.appointmentId) {
            currentPersonId = personId
        }
        await loadSchedule(entity.scheduleId)
        loadStatus()
    })

    let lastPersonId = null
    $: {
        if (personId !== lastPersonId) {
            console.log('changed')
            lastPersonId = personId
            if (!personId) entity.items = []
            entity = entity
            newItemCancelled()
        }
    }

    function onScheduleChange() {
        entity.items = []
        loadSchedule(entity.scheduleId)
        setState('ready')
    }
</script>

<LoadSpinner {show} />
{#if entity}
    <div class="root">
        <div class="appointment-form">
            <div class="config-box mt-3 mx-3">
                <ZContainer class="ap-form-block_bar">
                    <Zinput
                        size={4}
                        label="Agenda"
                        disabled={!!entity.appointmentId}
                        type="select"
                        options={scheduleOptions}
                        bind:value={entity.scheduleId}
                        on:change={onScheduleChange}
                    ></Zinput>
                    <Zinput size={3} label="Início" type="time" bind:value={entity.start} on:change={startTimeChanged}></Zinput>
                    <Zinput size={3} acts={[timeAuto]} label="Término" type="time" bind:value={entity.end} on:change={endTimeChanged}></Zinput>

                    <ZContainerCol size={2} sizeM={12}>
                        {#if window.store.token.permissions.scheduler.scheduler.write && showActs}
                            <div class="d-flex h-mobile acts">
                                {#if entity.appointmentId}
                                    <Zbutton growM={true} class="me-2" grow={true} label="Excluir" color="red" on:click={dispatchRemove}></Zbutton>
                                {:else}
                                    <Zbutton growM={true} class="me-2" grow={true} label="Cancelar" on:click={dispatchRemove}></Zbutton>
                                {/if}
                                <!-- <Zbutton grow={true} disabled={!isDirty || state == 'typing'} label="Salvar" color="primary" on:click={dispatchSave}></Zbutton> -->
                            </div>
                        {/if}
                    </ZContainerCol>
                </ZContainer>
                <div class="p-3">
                    {#if entity.items?.length > 0 || state == 'typing'}
                        <div>
                            <ZContainer class="liner">
                                <ZContainerCol size={3}>Atendente</ZContainerCol>
                                <ZContainerCol>Serviço</ZContainerCol>
                            </ZContainer>
                            <div class="grid-body">
                                {#each entity.items as item}
                                    <div transition:slide|global={{ duration: 200 }}>
                                        <AppointmentFormItems
                                            status={entity.status}
                                            allowed={schedule?.attendants}
                                            class="py-1 liner"
                                            bind:appointmentItem={item}
                                            personId={entity.personId}
                                            scheduleId={entity?.scheduleId}
                                            on:cancel={itemUpdated}
                                            on:added={itemUpdated}
                                        />
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/if}
                    <div class="">
                        {#if state == 'typing'}
                            <div class="grid-body mt-2 mb-2">
                                <AppointmentFormItems
                                    status={entity.status}
                                    allowed={schedule?.attendants}
                                    class="py-1"
                                    on:cancel={newItemCancelled}
                                    on:added={newItemAdded}
                                    scheduleId={entity?.scheduleId}
                                    personId={entity.personId}
                                    userId={lastUserId}
                                />
                            </div>
                        {:else}
                            <AppointmentFormBlockSugestions bind:this={sugest} {entity} {schedule} on:add={onAddSugestion}></AppointmentFormBlockSugestions>
                        {/if}
                        <div class="add-service mt-2">
                            <Zbutton
                                disabled={!showAddService || state == 'typing'}
                                class="me-2"
                                color="primary"
                                label="Serviço"
                                on:click={newAppointmentItem}
                                width="165px"
                                icon={faPlus}
                            ></Zbutton>
                        </div>
                    </div>

                    {#if showResources}
                        {#if entity.resources?.length > 0 || newResource}
                            <div class="grid-head h-mobile mt-4">
                                <div class="row ps-0">
                                    <div class="col mt-0">Sala / equipamentos bloqueados</div>
                                    <div class="col-md-2 mt-0" />
                                </div>
                            </div>
                            <div class="grid-body">
                                {#each entity.resources as resource}
                                    <AppointmentFormResources
                                        class="py-1 liner"
                                        locked={lockedIDs}
                                        bind:appointmentResource={resource}
                                        on:edit={refreshLockedResources}
                                        on:cancel={resourceUpdated}
                                        on:added={resourceUpdated}
                                    />
                                {/each}
                            </div>
                        {/if}
                        <div class="mt-2 mb-2">
                            {#if newResource}
                                <div class="grid-body">
                                    <AppointmentFormResources class="py-1" locked={lockedIDs} on:cancel={newResourceCancelled} on:added={newResourceAdded} />
                                </div>
                            {:else}
                                <div class="add-service">
                                    <Zbutton color="green" width="165px" label="Bloquear recurso" on:click={newResourceItem}></Zbutton>
                                </div>
                            {/if}
                        </div>
                    {/if}

                    {#if entity.consumption?.length > 0 || newConsumption}
                        <div class="grid-head h-mobile mt-4">
                            <div class="row ps-0">
                                <div class="col mt-0">Consumo de insumos</div>
                                <div class="col-md-2 mt-0" />
                            </div>
                        </div>
                        <div class="grid-body">
                            {#each entity.consumption || [] as consumption}
                                <AppointmentFormConsumption
                                    class="py-1 liner"
                                    items={entity.items}
                                    bind:appointmentConsumption={consumption}
                                    on:cancel={consumptionUpdated}
                                    on:added={consumptionUpdated}
                                />
                            {/each}
                        </div>
                    {/if}
                    <div class="mt-2 mb-2">
                        {#if newConsumption}
                            <div class="grid-body">
                                <AppointmentFormConsumption
                                    class="py-1"
                                    items={entity.items}
                                    on:cancel={newConsumptionCancelled}
                                    on:added={newConsumptionAdded}
                                />
                            </div>
                        {:else}
                            <div class="add-service">
                                <Zbutton color="green" width="165px" icon={faPlus} label="consumo" on:click={newConsumptionItem}></Zbutton>
                            </div>
                        {/if}
                    </div>

                    <div class="h-mobile mt-4">
                        <InlineSelect class="ap-form-block_status" small={true} options={appointmentStatus} bind:value={entity.status} />
                        <div class="-status-helper text-muted">{AppointmentStatusHelper[entity.status]}</div>
                    </div>
                    <div class="h-full mt-4">
                        <Zinput type="select" bind:value={entity.status} options={appointmentStatus} label="Status" />
                    </div>
                    <div class="mt-2 pb-2">
                        <Zinput class="ap_anotacoes" label="Anotações" type="textarea" bind:value={entity.notes} rows={2}></Zinput>
                    </div>

                    {#if window.store.token.permissions.scheduler.scheduler.write && showActs}
                        <div class="d-flex h-full acts">
                            {#if entity.appointmentId}
                                <Zbutton growM={true} class="mt-2" grow={true} label="Excluir" color="red" on:click={dispatchRemove}></Zbutton>
                            {:else}
                                <Zbutton growM={true} class="mt-2" grow={true} label="Cancelar" on:click={dispatchRemove}></Zbutton>
                            {/if}
                            <!-- <Zbutton grow={true} disabled={!isDirty || state == 'typing'} label="Salvar" color="primary" on:click={dispatchSave}></Zbutton> -->
                        </div>
                    {/if}

                    {#if !entity.appointmentId || entity.recurrencies?.length > 0}
                        <div class="btn btn-link mt-1 btn-recurring ps-0" on:click={() => (showRecurrency = !showRecurrency)}>
                            {showRecurrency ? 'Agendamento recorrente ' : 'Agendamento recorrente '}<Fa icon={showRecurrency ? faArrowUp : faArrowDown} />
                        </div>
                        {#if showRecurrency}
                            {#if !entity.appointmentId && entity.repeat}
                                <ZContainer gapRow="8px" gapCol="8px" class="mb-2">
                                    <Zinput
                                        size={3}
                                        sizeM={12}
                                        label="Repetir por mais"
                                        type="select"
                                        options={repeatQuantities}
                                        bind:value={entity.repeat.quantity}
                                    />
                                    <Zinput size={5} sizeM={12} label="Frequência" type="select" options={periods} bind:value={entity.repeat.periodicity} />
                                    <Zinput
                                        size={12}
                                        label="Repetir apenas nesses dias"
                                        type="inlineselect_2"
                                        multi={true}
                                        options={days}
                                        bind:value={entity.repeat.weekDays}
                                    />
                                </ZContainer>
                            {:else if entity?.recurrencies?.length > 0}
                                <div class="grid-recurrencies mb-2">
                                    {#each entity.recurrencies as recurrency, i}
                                        <div class="row">
                                            <div class="col col-md-5">
                                                {recurrency.groupPos}/{recurrency.groupTotal} -
                                                {dayjs(recurrency.start).format('DD/MM/YYYY HH:mm')}
                                                <span class="h-mobile">
                                                    até
                                                    {dayjs(recurrency.end).format('HH:mm')}
                                                    {' (' +
                                                        ['domingo', 'Segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'][
                                                            new Date(recurrency.start).getDay()
                                                        ] +
                                                        ')'}
                                                </span>
                                            </div>
                                            <div class="col col-md h-mobile">
                                                <div class="{recurrency.status} recurrency-badge">
                                                    {AppointmentStatusFriendly[recurrency.status]}
                                                </div>
                                            </div>
                                            <div class="col-2 col-md-2 text-end">
                                                {#if recurrency.appointmentId === entity.appointmentId}
                                                    atual
                                                {:else}
                                                    <button
                                                        class="btn btn-sm btn-warning btn-rec-edit"
                                                        on:click={() => dispatchRemoveAnother(recurrency.appointmentId)}><Fa icon={faTrashAlt} /></button
                                                    >
                                                    <button
                                                        class="btn btn-sm btn-secondary btn-rec-edit"
                                                        on:click={() => {
                                                            dispatchEditAnother(recurrency.appointmentId)
                                                        }}
                                                        ><Fa icon={faEdit} />
                                                        <span class="h-mobile">Editar</span></button
                                                    >
                                                {/if}
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                            {/if}
                        {/if}
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .appointment-form {
        width: 100%;
    }
    .config-box {
        border: 1px solid #32886666;
        padding: 0px;
        margin-top: 24px !important;
    }
    .row {
        padding: 0px 16px;
    }

    .btn-recurring {
        opacity: 0.7;
    }

    .appointment-form .-auto {
        color: #666;
        margin-left: 4px;
        margin-right: 4px;
        padding: 0px 6px;
        background: #444;
    }
    :global(.appointment-form_auto-on),
    :global(.appointment-form_auto-on:focus),
    :global(.appointment-form_auto-on:active) {
        color: #fff;
        background: #328866 !important;
    }
    :global(.appointment-form_auto-off),
    :global(.appointment-form_auto-off:focus),
    :global(.appointment-form_auto-off:active) {
        color: #777 !important;
        background: #444 !important;
    }
    .appointment-form .-input-end {
        padding-left: 0px;
    }
    .appointment-form .-status-helper {
        padding-top: 1px;
        padding-left: 1px;
        font-size: 13px;
    }
    .grid-recurrencies {
        opacity: 0.8;
    }
    .grid-recurrencies .row > * {
        border-bottom: 1px solid #99999922 !important;
        padding-top: 1px;
        padding-bottom: 2px;
    }
    .btn-rec-edit {
        padding: 0px 4px;
    }

    .last-added {
        border: 1px solid white !important;
    }

    .recurrency-badge {
        border-left: unset !important;
        width: fit-content;
        font-size: 13px;
        border-radius: 4px;
        padding: 1px 4px 0px 4px;
    }

    :global(.ap_anotacoes) {
        background-color: #88888810 !important;
        border-color: #88888820 !important;
        font-size: 15px;
    }

    :global(.ap-form-block_bar) {
        background-color: #88888833;
        padding: 8px;
        border-radius: 5px 5px 0px 0px;
    }

    :global(.ap-form-block_status .btn-secondary:not(.active)) {
        background-color: #88888822;
        opacity: 0.7;
    }

    @media (max-width: 900px) {
        .root {
            display: flex;
        }
        .grid-act {
            margin-top: 16px;
            background-color: unset;
            display: flex;
            justify-content: space-around;
        }
        .btn-time {
            margin-left: 0px !important;
        }
        .person-select {
            margin-bottom: 4px;
        }

        .acts {
            width: 100%;
        }
        .add-service {
            align-items: center;
            justify-content: center;
            flex-direction: column;
        }
    }
</style>
